'use client';
/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useMemo } from 'react';
import { pushDataLayer } from '../pushDataLayer';
import { PageType, VerticalType } from '../types';
import { BREADCRUMB_TRANSLATIONS } from './Breadcrumb.translations';
import { toJsonLd } from './toJsonLd';
import { UrlPrefixEnum } from '$/(cms)/_modules/cmsService/types';
import {
  getCmsPageLink,
  getCmsPagePublicPath,
  getHomePagePath,
  toCmsAbsUrl,
} from '$/(cms)/_modules/routing';
import { FeLink } from '$/components/FeLink';
import HomeIcon from '$/components/icons/HomeIcon';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

interface BreadcrumbItem {
  title: string;
  link: string;
}

interface BreadcrumbProps {
  vertical?: VerticalType;
  darkmode?: boolean;
  pageType: PageType;
  pageTitle: string;
  pageSlug?: string;
  urlPrefix?: UrlPrefixEnum;
  isBlogLanding?: boolean;
  isBlogVerticalLanding?: boolean;
  // a flagged page's last item links its root (CMS-2098)
  isHomepage?: boolean;
  isVerticalLanding?: boolean;
}

function pushVerticalBreadcrumb(
  bcs: BreadcrumbItem[],
  vertical: VerticalType | undefined,
  link: string,
  staticText: Record<string, string>,
) {
  const verticalTitleMap: Partial<Record<VerticalType, string>> = {
    CREDIT_CARD: staticText.breadcrumbs_credit_cards,
    PERSONAL_LOANS: staticText.breadcrumbs_personal_loans,
    BANKING: staticText.breadcrumbs_banking,
    INVESTMENTS: staticText.breadcrumbs_investment,
    TRAVEL_INSURANCE: staticText.breadcrumbs_travel_insurance,
  };
  const title = vertical ? verticalTitleMap[vertical] : undefined;
  if (title) {
    bcs.push({ link, title });
  }
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  vertical,
  darkmode = false,
  pageType,
  pageTitle,
  pageSlug,
  urlPrefix,
  isBlogLanding,
  isBlogVerticalLanding,
  isHomepage,
  isVerticalLanding,
}) => {
  const context = useClientContext();
  const { brand, langPrefix, host } = context;
  const staticText = getClientTranslations(BREADCRUMB_TRANSLATIONS, context);
  const colorClass = darkmode ? 'text-text/on-dark' : 'text-text/primary';

  const handleClick = (object: BreadcrumbItem, index: number) => {
    if (index < breadcrumbs.length - 1) {
      pushDataLayer({
        event: 'navigate',
        component_type: 'breadcrumb',
        component_title: object.title,
        action: 'click',
        index: index,
      });
    }
  };

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    const bcs: BreadcrumbItem[] = [
      // Step 1: Home
      {
        link: getHomePagePath(langPrefix),
        title:
          pageType === 'product'
            ? staticText.breadcrumb_start
            : staticText.breadcrumbs_home,
      },
    ];

    // Step 2: BlogLanding — only "Blog" as the final breadcrumb
    if (isBlogLanding) {
      bcs.push({
        link: `${langPrefix}/blog`,
        title: staticText.breadcrumbs_blog,
      });
      return bcs;
    }

    // Step 2: Vertical name
    // For articles: links to BlogVerticalLanding (/<vertical>/blog)
    // For other pages: links to vertical landing page (/<vertical>)
    const verticalLink = getCmsPageLink({
      pageType,
      vertical,
      langPrefix,
      brand,
      ...(pageType !== 'article' && { urlPrefix }),
    });
    pushVerticalBreadcrumb(bcs, vertical, verticalLink, staticText);

    // Step 3: Blog breadcrumb (articles only) — links to BlogLanding (/blog)
    if (pageType === 'article') {
      const blogLink = getCmsPageLink({
        pageType: 'article',
        langPrefix,
        brand,
      });
      bcs.push({ link: blogLink, title: staticText.breadcrumbs_blog });

      if (isBlogVerticalLanding) return bcs;
    }

    // Step 4: Page title
    bcs.push({
      title: pageTitle,
      link: getCmsPagePublicPath({
        brand,
        pageType,
        vertical,
        slug: pageSlug,
        langPrefix,
        urlPrefix,
        isHomepage,
        isVerticalLanding,
      }),
    });

    return bcs;
  }, [
    pageSlug,
    pageTitle,
    pageType,
    vertical,
    isBlogLanding,
    isBlogVerticalLanding,
    isHomepage,
    isVerticalLanding,
  ]);

  const setSchemaMarkupBreadcrumbs = (breadcrumbs: BreadcrumbItem[]) => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': toCmsAbsUrl(host, b.link),
            name: b.title,
          },
        };
      }),
    };

    return (
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: toJsonLd(schema) }}
      />
    );
  };

  return (
    <div className="no-scrollbar overflow-x-auto" data-component="breadcrumb">
      {setSchemaMarkupBreadcrumbs(breadcrumbs)}
      <div className="flex flex-nowrap items-center gap-2">
        {breadcrumbs.map((b, index) => (
          // a flagged page's last item can share its link, and maybe its title, with an earlier item
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`${index}_${b.link}`}>
            <FeLink
              key={`${b.link}_${b.title}`}
              href={b.link}
              className={`min-w-0 ${index === breadcrumbs.length - 1 ? 'max-w-fit' : 'shrink-0'} truncate font-medium opacity-80 ${colorClass} text-font-size/breadcrumb leading-line-height/breadcrumb`}
              aria-label={index === 0 ? b.title : undefined}
              onClick={(e) => {
                e.stopPropagation();
                handleClick(b, index);
              }}
            >
              {index === 0 ? (
                <HomeIcon
                  // eslint-disable-next-line react/no-array-index-key, tailwindcss/no-arbitrary-value
                  className={`size-[--shapes-icon-size-sm] ${colorClass}`}
                />
              ) : (
                b.title
              )}
            </FeLink>
            {index < breadcrumbs.length - 1 && (
              <span
                className={`font-medium opacity-80 ${colorClass} text-font-size/breadcrumb leading-line-height/breadcrumb`}
              >
                /
              </span>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
};
